import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Select } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { useHaulersQuery } from '~/modules/transportation/hooks/queries/useHaulersQuery';
import { $organization } from '~/shared/providers/auth/identity.localState';

interface HaulerSelectProps {
  label: string;
  value: string;
  onChange: (value: React.Key | null) => void;
  isRequired?: boolean;
  isDisabled?: boolean;
  onHaulerChange?: (haulerId: string | null) => void;
}

export const HaulerSelect = ({
  label,
  value,
  onChange,
  isRequired,
  isDisabled,
  onHaulerChange,
}: HaulerSelectProps) => {
  const t = useTranslations();
  const organization = useAtomValue($organization);

  const { data } = useHaulersQuery();

  const selectOptions = React.useMemo(() => {
    const externalHaulers =
      data?.haulers?.map((hauler) => ({
        key: hauler.id,
        label: hauler.name,
      })) ?? [];

    if (organization) {
      return [
        { key: organization.id, label: organization.name },
        ...externalHaulers,
      ];
    }
    return externalHaulers;
  }, [data?.haulers, organization]);

  const handleSelectionChange = React.useCallback(
    (key: React.Key | null | undefined) => {
      onChange(key ?? null);
      if (onHaulerChange) {
        onHaulerChange(key === '' ? null : key?.toString() || null);
      }
    },
    [onChange, onHaulerChange],
  );

  return (
    <Select
      label={label}
      options={selectOptions}
      value={value}
      isRequired={isRequired}
      isDisabled={isDisabled}
      onChange={handleSelectionChange}
      placeholder={t.servicesView.form.placeholders.hauler}
      className="@HaulerSelect"
    />
  );
};
